import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enIN } from 'date-fns/locale';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { createDeliveryChallan, updateDeliveryChallan } from '../../store/deliveryChallanSlice';
import { fetchColleges } from '../../store/collegeSlice';

const DeliveryChallanForm = ({ challan, onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.deliveryChallan);
  const { list: colleges } = useSelector((state) => state.colleges);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    control,
  } = useForm({
    defaultValues: {
      challanNumber: '',
      collegeId: '',
      date: new Date(),
      details: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
      amount: 0,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'details',
  });

  const watchedDate = watch('date');
  const watchedDetails = watch('details');

  useEffect(() => {
    dispatch(fetchColleges({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (challan) {
      reset({
        challanNumber: challan.challanNumber || '',
        collegeId: challan.collegeId?._id || '',
        date: new Date(challan.date) || new Date(),
        details: challan.details || [{ description: '', quantity: 1, rate: 0, amount: 0 }],
        amount: challan.amount || 0,
      });
    }
  }, [challan, reset]);

  // Calculate total amount when details change
  useEffect(() => {
    const total = watchedDetails?.reduce((sum, detail) => {
      const amount = (detail.quantity || 0) * (detail.rate || 0);
      return sum + amount;
    }, 0) || 0;
    setValue('amount', total);
  }, [watchedDetails, setValue]);

  const calculateRowAmount = (index) => {
    const detail = watchedDetails[index];
    if (detail) {
      const amount = (detail.quantity || 0) * (detail.rate || 0);
      setValue(`details.${index}.amount`, amount);
      return amount;
    }
    return 0;
  };

  const addDetailRow = () => {
    append({ description: '', quantity: 1, rate: 0, amount: 0 });
  };

  const removeDetailRow = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const onSubmit = async (data) => {
    try {
      const formData = {
        ...data,
        date: data.date.toISOString(),
        details: data.details.map(detail => ({
          ...detail,
          amount: detail.quantity * detail.rate
        }))
      };

      if (challan) {
        await dispatch(updateDeliveryChallan({ id: challan._id, data: formData })).unwrap();
        onSuccess('Delivery challan updated successfully');
      } else {
        await dispatch(createDeliveryChallan(formData)).unwrap();
        onSuccess('Delivery challan created successfully');
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Header Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <ReceiptIcon sx={{ mr: 1 }} />
                  Challan Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register('challanNumber', { required: 'Challan Number is required' })}
                      fullWidth
                      label="Challan Number"
                      error={!!errors.challanNumber}
                      helperText={errors.challanNumber?.message}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Date"
                      value={watchedDate}
                      onChange={(newValue) => setValue('date', newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.date}
                          helperText={errors.date?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!errors.collegeId}>
                      <InputLabel>College</InputLabel>
                      <Select
                        {...register('collegeId', { required: 'College is required' })}
                        label="College"
                        defaultValue=""
                      >
                        {colleges.map((college) => (
                          <MenuItem key={college._id} value={college._id}>
                            {college.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Details Table */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Item Details
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addDetailRow}
                    size="small"
                  >
                    Add Item
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="center" width={100}>Quantity</TableCell>
                        <TableCell align="center" width={120}>Rate (₹)</TableCell>
                        <TableCell align="center" width={120}>Amount (₹)</TableCell>
                        <TableCell align="center" width={80}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field, index) => (
                        <TableRow key={field.id}>
                          <TableCell>
                            <TextField
                              {...register(`details.${index}.description`, { 
                                required: 'Description is required' 
                              })}
                              fullWidth
                              size="small"
                              placeholder="Item description"
                              error={!!errors.details?.[index]?.description}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              {...register(`details.${index}.quantity`, { 
                                required: true,
                                min: 1,
                                onChange: () => calculateRowAmount(index)
                              })}
                              type="number"
                              size="small"
                              inputProps={{ min: 1 }}
                              error={!!errors.details?.[index]?.quantity}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              {...register(`details.${index}.rate`, { 
                                required: true,
                                min: 0,
                                onChange: () => calculateRowAmount(index)
                              })}
                              type="number"
                              size="small"
                              inputProps={{ min: 0, step: 0.01 }}
                              error={!!errors.details?.[index]?.rate}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ₹{((watchedDetails[index]?.quantity || 0) * (watchedDetails[index]?.rate || 0)).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeDetailRow(index)}
                              disabled={fields.length === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total Amount: ₹{(watchedDetails?.reduce((sum, detail) => 
                      sum + ((detail.quantity || 0) * (detail.rate || 0)), 0) || 0
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || fields.length === 0}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {challan ? 'Update Challan' : 'Create Challan'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DeliveryChallanForm;